<template>
    <div
        @blur="showPropertySelector = false"
        tabindex="0"
        v-click-outside.stop="() => (showPropertySelector = false)"
    >
        <app-input
            v-bind="$attrs"
            icon="target"
            icon-click-emit="location"
            :value="value"
            type="text"
            @focus="showPropertySelector = true"
            @location="$emit('location')"
            @keyup-enter="submit"
            @input="onInput"
        >
            <template v-slot:before v-if="$slots.before">
                <slot name="before"></slot>
            </template>
            <template v-slot:afterInput>
                <div
                    class="text-black absolute border border-r-darkgreen bg-white shadow-lg max-h-60 lg:max-h-80 overflow-y-auto z-20"
                    :class="[dropdownClasses]"
                    v-if="showPropertySelector"
                >
                    <div
                        v-for="group in propertyGroups"
                        :key="group.state || ''"
                    >
                        <div
                            :class="[propertyListClasses]"
                            class="py-3 px-4 font-bold"
                        >
                            <template v-if="group.state === null">
                                {{ $t("home.booking.other_roatels") }}
                            </template>
                            <template v-else-if="isFiltering">
                                {{ group.state }}
                            </template>
                            <template v-else>
                                {{ group.state }} -
                                {{ $t("new_reservation.find_roatel_all") }}
                            </template>
                        </div>
                        <div
                            v-for="property in group.properties"
                            :key="property.uuid"
                            class="group flex flex-row gap-2 items-center hover:bg-r-green hover:text-white hover:cursor-pointer"
                            :class="[propertyListClasses]"
                            @click.stop="selectProperty(property)"
                        >
                            <div class="rounded-xl">
                                <icon
                                    icon="roatel"
                                    padding=""
                                    :is-rounded="false"
                                    size="w-5"
                                    background="bg-transparent"
                                    class="text-r-green group-hover:text-white stroke-transparent"
                                ></icon>
                            </div>
                            <div class="text-lg leading-4">
                                {{ property.name }}
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="showSearchAround"
                        class="group hover:bg-r-green hover:text-white hover:cursor-pointer"
                        :class="{
                            'border-t border-r-gray-light':
                                propertyGroups.length > 0,
                        }"
                        @click="searchAround"
                    >
                        <i18n
                            path="home.booking.search_around"
                            tag="div"
                            class="py-3 px-4 font-bold"
                            :class="[propertyListClasses]"
                        >
                            <span
                                class="group-hover:text-white text-r-green hover:text-white underline underline-offset-2 font-bold"
                                >{{ value }}</span
                            >
                            >
                        </i18n>
                    </div>
                </div>
            </template>
        </app-input>
    </div>
</template>
<script>
import AppInput from "@guest/common/Input.vue";
import Icon from "@guest/common/Icon.vue";
import _ from "lodash";
import * as vClickOutside from "v-click-outside-x";
import {
    createPropertySearch,
    groupPropertiesByState,
} from "@/guest/utils/propertySearch";

/**
 * Text input with a dropdown of all Roatels (grouped by federal state, fuzzy
 * filtered while typing) and a "search around <text>" entry. Used on the guest
 * home page and in the booking flow's property search.
 *
 * Attributes that are not props (label, placeholder, ...) go to the input.
 * Emits `input` (text), `select` (property), `search-around` (text) and
 * `location` (locate icon clicked).
 */
export default {
    inheritAttrs: false,
    directives: {
        clickOutside: vClickOutside.directive,
    },
    components: {
        AppInput,
        Icon,
    },
    props: {
        value: {
            type: String,
            required: false,
            default: "",
        },
        properties: {
            type: Array,
            required: false,
            default: () => [],
        },
        dropdownClasses: {
            type: String,
            required: false,
            default: "w-3/4 lg:w-full lg:top-20",
        },
    },
    data: function () {
        return {
            // Debounced copy of the input that drives the list filter.
            searchTerm: this.value,
            setBySelection: false,
            showPropertySelector: false,
            propertyListClasses: "py-3 px-5 ",
        };
    },
    watch: {
        value(value) {
            this.updateSearchTerm(value);
        },
    },
    computed: {
        searchProperties() {
            return createPropertySearch(this.properties);
        },
        // The full list is shown for an empty input and right after a
        // selection (the input then holds the selected property's name).
        isFiltering() {
            return (
                !this.setBySelection &&
                this.value.trim() !== "" &&
                this.searchTerm.trim() !== ""
            );
        },
        propertyGroups() {
            return groupPropertiesByState(
                this.isFiltering
                    ? this.searchProperties(this.searchTerm)
                    : this.properties
            );
        },
        showSearchAround() {
            return this.value.trim() !== "" && !this.setBySelection;
        },
    },
    methods: {
        onInput(value) {
            this.setBySelection = false;
            // Enter closes the list without the input losing focus.
            this.showPropertySelector = true;
            this.$emit("input", value);
        },
        selectProperty(property) {
            this.$emit("input", property.name);
            this.showPropertySelector = false;
            this.setBySelection = true;
            this.$emit("select", property);
        },
        searchAround() {
            this.showPropertySelector = false;
            this.$emit("search-around", this.value.trim());
        },
        // Enter picks the only matching property, otherwise it starts the
        // search around the entered text.
        submit() {
            const term = this.value.trim();
            if (this.setBySelection || term === "") {
                return;
            }
            const matches = this.searchProperties(term);
            if (matches.length === 1) {
                this.selectProperty(matches[0]);
                return;
            }
            if (term.length >= 2) {
                this.searchAround();
            }
        },
    },
    created() {
        this.updateSearchTerm = _.debounce((value) => {
            this.searchTerm = value;
        }, 150);
    },
    beforeDestroy() {
        this.updateSearchTerm.cancel();
    },
};
</script>
